import React, { useEffect, useState } from "react";
import { Package2 } from "lucide-react";
import OrdersList from "../components/Orders/list";
import OrderDetails from "../components/Orders/details";
import { Order } from "../types/Order";
import { useTranslation } from "react-i18next";
import BaseApi from "../services/base-api";
import { useToast } from "../context/ToastContext";
import { useLocation, useNavigate } from "react-router-dom";
// import { verifyPaymentStatus } from "../utils/paymentUtils";

const MyOrders: React.FC = () => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState<string | null>(null);
  const { t } = useTranslation();
  const { success, err } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const fetchOrderById = async (orderId: string): Promise<Order | null> => {
    try {
      const directRes = await BaseApi.get(`/orders/my-orders/${orderId}`);
      if (directRes?.success && directRes?.data?.id) {
        return directRes.data as Order;
      }
    } catch (error) {
      // Fallback to paginated listing if direct endpoint is unavailable
    }

    let page = 1;
    const limit = 50;
    while (page <= 10) {
      try {
        const res = await BaseApi.get(
          `/orders/my-orders?page=${page}&limit=${limit}`,
        );
        const orders: Order[] = Array.isArray(res?.data) ? res.data : [];
        const targetOrder = orders.find((order) => order.id === orderId);
        if (targetOrder) {
          return targetOrder;
        }

        const totalPages =
          Number(res?.pagination?.totalPages) ||
          Math.ceil(Number(res?.pagination?.totalItems || 0) / limit) ||
          1;
        if (page >= totalPages) break;
      } catch (error) {
        break;
      }
      page += 1;
    }

    return null;
  };

  useEffect(() => {
    const state = location.state as
      | {
          paymentFailed?: boolean;
          orderId?: string;
          message?: string;
        }
      | undefined;

    if (!state?.paymentFailed || !state?.orderId) return;

    const openFailedOrderDetails = async () => {
      err(state.message || t("Payment failed. Please try again."));
      const order = await fetchOrderById(state.orderId!);
      if (order) {
        setSelectedOrder(order);
      } else {
        err(t("Unable to open order details automatically"));
      }
      navigate(location.pathname, { replace: true, state: null });
    };

    openFailedOrderDetails();
  }, [location.state, location.pathname, navigate, err, t]);

  const handleDownloadInvoice = async (orderId: string) => {
    try {
      setInvoiceLoading(orderId);

      // First check if invoice exists for this order
      const checkRes = await BaseApi.get(`/invoices/order/${orderId}`);

      if (!checkRes.success || !checkRes.data) {
        err(t("Invoice not available for this order"));
        return;
      }

      const invoiceId = checkRes.data.id;

      // Download the invoice
      const res = await BaseApi.get(`/invoices/${invoiceId}/download`, {
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `invoice-${checkRes.data.invoiceNumber}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      success(t("Invoice downloaded successfully"));
    } catch (error) {
      console.error("Error downloading invoice:", error);
    } finally {
      setInvoiceLoading(null);
    }
  };

  const handleViewInvoice = async (orderId: string) => {
    //   try {
    //     setInvoiceLoading(orderId);
    //     // Get invoice details
    //     const res = await BaseApi.get(`/invoices/order/${orderId}`);
    //     if (res.success && res.data) {
    //       // You can show invoice in a modal or redirect to invoice page
    //       // For now, we'll just download it
    //       await handleDownloadInvoice(orderId);
    //     } else {
    //       err(t("Invoice not available for this order"));
    //     }
    //   } catch (error) {
    //     console.error("Error viewing invoice:", error);
    //     err(t("Failed to view invoice"));
    //   } finally {
    //     setInvoiceLoading(null);
    //   }
  };

  // const handleRetryPayment = (order: Order) => {
  //   setSelectedOrder(order);
  // };

  // const handleVerifyPayment = async (orderId: string) => {
  //   const result = await verifyPaymentStatus(orderId);
  //   // Update order status based on result
  // };

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="max-w-6xl mx-auto">
        {!selectedOrder ? (
          <>
            {/* Header */}
            <div
              className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-6"
              style={{ animation: "fadeIn 0.5s ease-out" }}
            >
              <div className="flex items-center gap-4">
                <div className="bg-black rounded-full p-4">
                  <Package2 className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">
                    {t("My Orders")}
                  </h1>
                  <p className="text-gray-600">
                    {t("Track and manage your orders")}
                  </p>
                </div>
              </div>
            </div>

            {/* Orders List */}
            <OrdersList
              onSelectOrder={setSelectedOrder}
              onDownloadInvoice={handleDownloadInvoice}
              onViewInvoice={handleViewInvoice}
              invoiceLoading={invoiceLoading}
            />
          </>
        ) : (
          <OrderDetails
            order={selectedOrder}
            onBack={() => setSelectedOrder(null)}
            onDownloadInvoice={handleDownloadInvoice}
            onViewInvoice={handleViewInvoice}
            invoiceLoading={invoiceLoading}
          />
        )}
      </div>
    </div>
  );
};

export default MyOrders;
